import { useAuth0 } from '@auth0/auth0-react';
import { AvatarV2, Modal } from '@cimplico/components';
import { Link } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useUserContext } from '../../providers/UserProvider';
import { useState } from 'react';
import { useFirm } from '../../queries/use-firm';
import { parseTitle } from '../../utilities/formatting';
import { Menu } from '@headlessui/react';
import UserAvatar from '../avatar/UserAvatar';
import FirmSelectDropdown from '../firm-select/FirmSelectDropdown';
import { useIntercom } from 'react-use-intercom';

type ProfileIconProps = {
    firstName: string;
    lastName: string;
    profileHexColour: string;
    avatarUrl?: string | null;
};

function ProfileIcon({ firstName, lastName, avatarUrl, profileHexColour }: ProfileIconProps): JSX.Element {
    const [switchFirm, setSwitchFirm] = useState(false);
    const { logout } = useAuth0();
    const { id: firmId, shortId } = useFirmContext();
    const { id } = useUserContext();
    const intercom = useIntercom();
    const firmQuery = useFirm(firmId);

    function randomColor(): string | undefined {
        throw new Error('Function not implemented.');
    }

    async function handleLogout(): Promise<void> {
        await logout({ logoutParams: { returnTo: window.location.origin } });
        if (intercom) {
            intercom.shutdown();
        }
    }

    return (
        <div data-intercom-target="header-user-profile-icon">
            <Modal isOpen={switchFirm === true} onClose={(): void => setSwitchFirm(false)}>
                <FirmSelectDropdown firmId={firmQuery.data?.firm.id || ''} />
            </Modal>
            <Menu>
                <Menu.Button className="relative focus:outline-none focus:ring-0">
                    <div className="cursor-pointer">
                        <UserAvatar user={{ firstName, lastName, avatarUrl, profileHexColour }} />
                    </div>
                </Menu.Button>
                <Menu.Items className="absolute top-14 right-2 z-40 focus:outline-none focus:ring-0 dark:text-gray-200 text-gray-700 bg-white dark:bg-gray-800 cursor-pointer grid items-center py-2 text-sm border border-gray-200 dark:border-white/[0.07] rounded-md">
                    {firmQuery.data && (
                        <Menu.Item disabled>
                            <div className="flex items-center py-2 pl-4 pr-8 border-b border-gray-200 dark:border-white/[0.07] cursor-default">
                                <div className="shrink-0">
                                    <AvatarV2
                                        alt={`${parseTitle(firmQuery.data.firm.name)}`}
                                        size="xs"
                                        color={firmQuery.data.firm.avatarHexColour}
                                    />
                                </div>
                                <p className="line-clamp-1 text-left pl-2">{firmQuery.data.firm.name}</p>
                            </div>
                        </Menu.Item>
                    )}
                    <Menu.Item>
                        {({ active }): JSX.Element => (
                            <Link to={{ pathname: `/${shortId}/users/${id}`, search: `?tab=profile` }}>
                                <p
                                    className={`${
                                        active && 'dark:bg-white/[0.09] bg-gray-100 text-gray-900 dark:text-white/[0.87]'
                                    } pl-4 pr-8 py-2`}
                                >
                                    Profile
                                </p>
                            </Link>
                        )}
                    </Menu.Item>
                    <Menu.Item>
                        {({ active }): JSX.Element => (
                            <Link
                                to={{ pathname: `/${shortId}/users/${id}`, search: `?tab=workpapers` }}
                                className="border-b border-gray-200 dark:border-white/[0.07]"
                            >
                                <p
                                    className={`${
                                        active && 'dark:bg-white/[0.09] bg-gray-100 text-gray-900 dark:text-white/[0.87]'
                                    } pl-4 pr-8 py-2`}
                                >
                                    Open Work
                                </p>
                            </Link>
                        )}
                    </Menu.Item>
                    <Menu.Item>
                        {({ active }): JSX.Element => (
                            <Link to={{ pathname: `/onboarding`, search: `?step=join-firm` }}>
                                <p
                                    className={`${
                                        active && 'dark:bg-white/[0.09] bg-gray-100 text-gray-900 dark:text-white/[0.87]'
                                    } pl-4 pr-8 py-2`}
                                >
                                    View Invitations
                                </p>
                            </Link>
                        )}
                    </Menu.Item>
                    <Menu.Item>
                        {({ active }): JSX.Element => (
                            <button
                                className={`${
                                    active && 'dark:bg-white/[0.09] bg-gray-100 text-gray-900 dark:text-white/[0.87]'
                                } border-b border-gray-200 dark:border-white/[0.07] pl-4 pr-8 py-2 text-left`}
                                onClick={(): void => setSwitchFirm(true)}
                            >
                                Switch Firms
                            </button>
                        )}
                    </Menu.Item>
                    <Menu.Item>
                        {({ active }): JSX.Element => (
                            <button
                                className={`${
                                    active && 'dark:bg-white/[0.09] bg-gray-100 text-gray-900 dark:text-white/[0.87]'
                                } pl-4 pr-8 py-2 text-left`}
                                onClick={handleLogout}
                            >
                                Logout
                            </button>
                        )}
                    </Menu.Item>
                </Menu.Items>
            </Menu>
        </div>
    );
}

export default ProfileIcon;
